import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Copy, MoreVertical, Star, Search, Filter, LayoutGrid, List, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, X, DollarSign, ShoppingBag, Percent, ArrowRight, Zap, ExternalLink, Archive, ChevronRight, Calculator } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
interface ServicesTabProps {
  showToast: (msg: string) => void;
  setActiveTab?: (tab: string) => void;
}
// --- MOCK DATA ---
const initialServices = [{
  id: 1,
  name: 'Разбор натальной карты',
  category: 'Натальная карта',
  price: 5000,
  duration: '60 мин',
  format: 'Видео-звонок',
  status: 'active',
  sales: 124,
  revenue: 620000,
  conversion: 18.5,
  rating: 4.9,
  returns: 2,
  isProblem: false
}, {
  id: 2,
  name: 'Соляр на год',
  category: 'Прогнозы',
  price: 4000,
  duration: '3 дня',
  format: 'Текстовый (PDF)',
  status: 'active',
  sales: 89,
  revenue: 356000,
  conversion: 12.3,
  rating: 4.7,
  returns: 1,
  isProblem: false
}, {
  id: 3,
  name: 'Синастрия (Совместимость)',
  category: 'Отношения',
  price: 6000,
  duration: '90 мин',
  format: 'Видео-звонок',
  status: 'active',
  sales: 45,
  revenue: 270000,
  conversion: 9.8,
  rating: 4.8,
  returns: 0,
  isProblem: false
}, {
  id: 4,
  name: 'Детский гороскоп',
  category: 'Натальная карта',
  price: 4500,
  duration: '5 дней',
  format: 'Текстовый (PDF)',
  status: 'draft',
  sales: 0,
  revenue: 0,
  conversion: 0,
  rating: 0,
  returns: 0,
  isProblem: true,
  problemText: 'Не заполнено описание'
}, {
  id: 5,
  name: 'Экспресс-ответ на вопрос',
  category: 'Консультации',
  price: 1500,
  duration: '24 часа',
  format: 'Чат',
  status: 'hidden',
  sales: 210,
  revenue: 315000,
  conversion: 22.1,
  rating: 4.6,
  returns: 5,
  isProblem: true,
  problemText: 'Высокий процент возвратов'
}];
export function ServicesTab({
  showToast,
  setActiveTab
}: ServicesTabProps) {
  // --- STATE ---
  const [services, setServices] = useState(initialServices);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Modals state
  const [creatingService, setCreatingService] = useState(false);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // --- COMPUTED DATA ---
  const filteredServices = useMemo(() => {
    return services.filter(s => {
      const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [services, searchQuery, filterStatus]);
  const totalRevenue = services.reduce((sum, s) => sum + s.revenue, 0);
  const totalSales = services.reduce((sum, s) => sum + s.sales, 0);
  const avgCheck = totalSales > 0 ? Math.round(totalRevenue / totalSales) : 0;
  const bestService = [...services].sort((a, b) => b.revenue - a.revenue)[0];
  const worstService = [...services].filter(s => s.status === 'active').sort((a, b) => a.revenue - b.revenue)[0];
  const problemServices = services.filter(s => s.isProblem);
  // --- HANDLERS ---
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredServices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredServices.map(s => s.id)));
    }
  };
  const toggleSelect = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleStatusToggle = (id: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setServices(services.map(s => {
      if (s.id === id) {
        const newStatus = s.status === 'active' ? 'hidden' : 'active';
        showToast(`Услуга "${s.name}" ${newStatus === 'active' ? 'опубликована' : 'скрыта'}`);
        return {
          ...s,
          status: newStatus
        };
      }
      return s;
    }));
    setActiveDropdown(null);
  };
  const handleDelete = (id: number) => {
    setServices(services.filter(s => s.id !== id));
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setDeleteConfirmId(null);
    showToast('Услуга удалена');
  };
  const handleDuplicate = (service: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const newService = {
      ...service,
      id: Date.now(),
      name: `${service.name} (Копия)`,
      status: 'draft',
      sales: 0,
      revenue: 0,
      returns: 0,
      isProblem: false
    };
    setServices([...services, newService]);
    showToast('Услуга дублирована');
    setActiveDropdown(null);
  };
  const handleBulkAction = (action: 'hide' | 'activate' | 'delete') => {
    if (action === 'delete') {
      setServices(services.filter(s => !selectedIds.has(s.id)));
      showToast(`Удалено услуг: ${selectedIds.size}`);
    } else {
      const newStatus = action === 'activate' ? 'active' : 'hidden';
      setServices(services.map(s => selectedIds.has(s.id) ? {
        ...s,
        status: newStatus
      } : s));
      showToast(`Статус изменен для ${selectedIds.size} услуг`);
    }
    setSelectedIds(new Set());
  };
  const navigateToOrders = (serviceName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showToast(`Переход к заказам: ${serviceName}`);
    if (setActiveTab) setActiveTab('orders');
  };
  const navigateToPromotion = (serviceName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showToast(`Переход к продвижению: ${serviceName}`);
    if (setActiveTab) setActiveTab('promotion');
  };
  // --- RENDERERS ---
  // 1. CREATE WIZARD MODAL
  const renderCreateWizard = () => {
    if (!creatingService) return null;
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-14531">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-14532">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-14533">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-14534">
              Создание новой услуги
            </h3>
            <button onClick={() => setCreatingService(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14535">
              <X className="w-6 h-6" data-id="element-14536" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-6" data-id="element-14537">
            <div className="space-y-4" data-id="element-14538">
              <div data-id="element-14539">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-14540">
                  Название услуги
                </label>
                <Input placeholder="Например: Разбор натальной карты" fullWidth data-id="element-14541" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-14542">
                <div data-id="element-14543">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-14544">
                    Категория
                  </label>
                  <select className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" data-id="element-14545">
                    <option data-id="element-14546">Натальная карта</option>
                    <option data-id="element-14547">Прогнозы</option>
                    <option data-id="element-14548">Отношения</option>
                    <option data-id="element-14549">Консультации</option>
                  </select>
                </div>
                <div data-id="element-14550">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-14551">
                    Формат
                  </label>
                  <select className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" data-id="element-14552">
                    <option data-id="element-14553">Видео-звонок</option>
                    <option data-id="element-14554">Текстовый (PDF)</option>
                    <option data-id="element-14555">Аудио-сообщение</option>
                    <option data-id="element-14556">Чат</option>
                  </select>
                </div>
              </div>

              {/* Calculator Block */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mt-6" data-id="element-14557">
                <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2 mb-3" data-id="element-14558">
                  <Calculator className="w-4 h-4" data-id="element-14559" /> Калькулятор дохода
                </h4>
                <div className="space-y-3" data-id="element-14560">
                  <div data-id="element-14561">
                    <label className="block text-xs font-medium text-indigo-700 mb-1" data-id="element-14562">
                      Цена для клиента (₽)
                    </label>
                    <Input type="number" defaultValue="5000" fullWidth className="bg-white" data-id="element-14563" />
                  </div>
                  <div className="flex justify-between items-center text-sm py-2 border-b border-indigo-200/50" data-id="element-14564">
                    <span className="text-indigo-600" data-id="element-14565">
                      Комиссия платформы (20%)
                    </span>
                    <span className="font-medium text-rose-600" data-id="element-14566">- 1 000 ₽</span>
                  </div>
                  <div className="flex justify-between items-center text-base font-bold pt-1" data-id="element-14567">
                    <span className="text-indigo-900" data-id="element-14568">Ваш чистый доход</span>
                    <span className="text-emerald-600" data-id="element-14569">4 000 ₽</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 sm:p-6 flex justify-end gap-3 z-10" data-id="element-14570">
            <Button variant="outline" onClick={() => setCreatingService(false)} data-id="element-14571">
              Отмена
            </Button>
            <Button variant="primary" onClick={() => {
            showToast('Услуга успешно создана и сохранена в черновики');
            setCreatingService(false);
          }} data-id="element-14572">
              Сохранить черновик
            </Button>
            <Button variant="primary" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
            showToast('Услуга успешно опубликована');
            setCreatingService(false);
          }} data-id="element-14573">
              Опубликовать
            </Button>
          </div>
        </div>
      </div>;
  };
  // 2. EDIT MODAL
  const renderEditModal = () => {
    if (!editingService) return null;
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-14574">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-14575">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-14576">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-14577">
              Редактирование: {editingService.name}
            </h3>
            <button onClick={() => setEditingService(null)} className="text-slate-400 hover:text-slate-600" data-id="element-14578">
              <X className="w-6 h-6" data-id="element-14579" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-4" data-id="element-14580">
            <div data-id="element-14581">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-14582">
                Название услуги
              </label>
              <Input defaultValue={editingService.name} fullWidth data-id="element-14583" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-14584">
              <div data-id="element-14585">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-14586">
                  Цена (₽)
                </label>
                <Input type="number" defaultValue={editingService.price} fullWidth data-id="element-14587" />
              </div>
              <div data-id="element-14588">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-14589">
                  Длительность
                </label>
                <Input defaultValue={editingService.duration} fullWidth data-id="element-14590" />
              </div>
            </div>
            {editingService.isProblem && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3 mt-4" data-id="element-14591">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-14592" />
                <div data-id="element-14593">
                  <h4 className="text-sm font-bold text-rose-900" data-id="element-14594">
                    Проблема: {editingService.problemText}
                  </h4>
                  <p className="text-xs text-rose-700 mt-1" data-id="element-14595">
                    Исправьте проблему, чтобы увеличить конверсию.
                  </p>
                </div>
              </div>}
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-14596">
            <Button variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => {
            setDeleteConfirmId(editingService.id);
            setEditingService(null);
          }} data-id="element-14597">
              Удалить
            </Button>
            <div className="flex gap-3" data-id="element-14598">
              <Button variant="outline" onClick={() => setEditingService(null)} data-id="element-14599">
                Отмена
              </Button>
              <Button variant="primary" onClick={() => {
              showToast('Изменения сохранены');
              setEditingService(null);
            }} data-id="element-14600">
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      </div>;
  };
  // 3. DELETE CONFIRM MODAL
  const renderDeleteConfirm = () => {
    if (!deleteConfirmId) return null;
    const service = services.find(s => s.id === deleteConfirmId);
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-14601">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl animate-in zoom-in-95 duration-200 text-center" data-id="element-14602">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-14603">
            <Trash2 className="w-8 h-8 text-rose-600" data-id="element-14604" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-14605">
            Удалить услугу?
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-14606">
            Вы уверены, что хотите удалить "{service?.name}"? Это действие
            нельзя отменить.
          </p>
          <div className="flex gap-3" data-id="element-14607">
            <Button variant="outline" fullWidth onClick={() => setDeleteConfirmId(null)} data-id="element-14608">
              Отмена
            </Button>
            <Button variant="primary" fullWidth className="bg-rose-600 hover:bg-rose-700" onClick={() => handleDelete(deleteConfirmId)} data-id="element-14609">
              Удалить
            </Button>
          </div>
        </div>
      </div>;
  };
  // --- MAIN RENDER ---
  return <div className="space-y-6 animate-in fade-in duration-300 pb-20" data-id="element-14610">
      {renderCreateWizard()}
      {renderEditModal()}
      {renderDeleteConfirm()}

      {/* 1. HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14611">
        <div data-id="element-14612">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14613">Услуги</h2>
          <p className="text-slate-500 text-sm" data-id="element-14614">
            Управление вашими консультациями и продуктами
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" data-id="element-14616" />} onClick={() => setCreatingService(true)} data-id="element-14615">
          Создать услугу
        </Button>
      </div>

      {/* 2. KPI BLOCK */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-14617">
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => setFilterStatus('all')} data-id="element-14618">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-14619">
            Общая выручка
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-14620">
            {totalRevenue.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] font-bold text-emerald-600 flex items-center" data-id="element-14621">
            <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-14622" /> +12% за месяц
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => setFilterStatus('all')} data-id="element-14623">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-14624">
            Продажи
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-14625">
            {totalSales}
          </div>
          <div className="text-[10px] font-bold text-emerald-600 flex items-center" data-id="element-14626">
            <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-14627" /> +5% за месяц
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" data-id="element-14628">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-14629">
            Средний чек
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-14630">
            {avgCheck.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] font-bold text-slate-500 flex items-center" data-id="element-14631">
            По всем услугам
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-emerald-300 cursor-pointer transition-colors bg-emerald-50/30" onClick={() => {
        setSearchQuery(bestService?.name || '');
      }} data-id="element-14632">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-14633">
            Лучшая услуга
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" title={bestService?.name} data-id="element-14634">
            {bestService?.name || '-'}
          </div>
          <div className="text-[10px] font-bold text-emerald-600 mt-1" data-id="element-14635">
            {bestService?.sales} продаж
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-rose-300 cursor-pointer transition-colors bg-rose-50/30" onClick={() => {
        setSearchQuery(worstService?.name || '');
      }} data-id="element-14636">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-14637">
            Худшая услуга
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" title={worstService?.name} data-id="element-14638">
            {worstService?.name || '-'}
          </div>
          <div className="text-[10px] font-bold text-rose-600 mt-1" data-id="element-14639">
            Нужно улучшить
          </div>
        </Card>
      </div>

      {/* 3. PROBLEM SERVICES ALERT */}
      {problemServices.length > 0 && <Card padding="md" className="border-rose-200 bg-rose-50/50" data-id="element-14640">
          <div className="flex items-center gap-2 mb-3" data-id="element-14641">
            <AlertCircle className="w-5 h-5 text-rose-600" data-id="element-14642" />
            <h3 className="text-base font-bold text-rose-900" data-id="element-14643">
              Проблемные услуги ({problemServices.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14644">
            {problemServices.map(s => <div key={`prob-${s.id}`} className="bg-white p-3 rounded-xl border border-rose-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 shadow-sm cursor-pointer hover:border-rose-300 transition-colors" onClick={() => setEditingService(s)} data-id="element-14645">
                <div data-id="element-14646">
                  <div className="text-sm font-bold text-slate-900" data-id="element-14647">
                    {s.name}
                  </div>
                  <div className="text-xs text-rose-600 mt-0.5" data-id="element-14648">
                    {s.problemText}
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto" data-id="element-14649">
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none text-rose-600 border-rose-200 hover:bg-rose-50" onClick={e => {
              e.stopPropagation();
              setEditingService(s);
            }} data-id="element-14650">
                    Исправить
                  </Button>
                  <Button variant="primary" size="sm" className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700" onClick={e => navigateToPromotion(s.name, e)} data-id="element-14651">
                    Реклама
                  </Button>
                </div>
              </div>)}
          </div>
        </Card>}

      {/* 4. REVENUE GROWTH RECOMMENDATIONS */}
      <Card padding="md" className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/50" data-id="element-14652">
        <div className="flex items-center gap-2 mb-3" data-id="element-14653">
          <Zap className="w-5 h-5 text-indigo-600" data-id="element-14654" />
          <h3 className="text-base font-bold text-indigo-900" data-id="element-14655">
            Точки роста дохода
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14656">
          <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col justify-between" data-id="element-14657">
            <div data-id="element-14658">
              <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-14659">
                Поднимите цену на "Разбор натальной карты"
              </div>
              <p className="text-xs text-slate-600 mb-3" data-id="element-14660">
                Конверсия 18.5% — это очень высоко. Вы можете безопасно поднять
                цену на 10-15%.
              </p>
            </div>
            <Button variant="outline" size="sm" className="w-full text-indigo-700 border-indigo-200" onClick={() => {
            const s = services.find(s => s.id === 1);
            if (s) setEditingService(s);
          }} data-id="element-14661">
              Применить
            </Button>
          </div>
          <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col justify-between" data-id="element-14662">
            <div data-id="element-14663">
              <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-14664">
                Создайте пакетную услугу
              </div>
              <p className="text-xs text-slate-600 mb-3" data-id="element-14665">
                Клиенты часто покупают Соляр после Натальной карты. Объедините
                их со скидкой 10%.
              </p>
            </div>
            <Button variant="primary" size="sm" className="w-full bg-indigo-600" onClick={() => setCreatingService(true)} data-id="element-14666">
              Создать пакет
            </Button>
          </div>
        </div>
      </Card>

      {/* 5. SEARCH, FILTERS & VIEW TOGGLE */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14667">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-14668">
          <div className="relative w-full sm:w-64" data-id="element-14669">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-14670" />
            <input type="text" placeholder="Поиск услуг..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14671" />
          </div>
          <Button variant={showFilters ? 'primary' : 'outline'} icon={<Filter className="w-4 h-4" data-id="element-14673" />} onClick={() => setShowFilters(!showFilters)} className="w-full sm:w-auto" data-id="element-14672">
            Фильтры
          </Button>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-lg w-full sm:w-auto" data-id="element-14674">
          <button onClick={() => setViewMode('table')} className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${viewMode === 'table' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-14675">
            <List className="w-4 h-4" data-id="element-14676" />{' '}
            <span className="hidden sm:inline" data-id="element-14677">Таблица</span>
          </button>
          <button onClick={() => setViewMode('cards')} className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${viewMode === 'cards' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-14678">
            <LayoutGrid className="w-4 h-4" data-id="element-14679" />{' '}
            <span className="hidden sm:inline" data-id="element-14680">Карточки</span>
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      {showFilters && <Card padding="sm" className="border-slate-200 bg-slate-50 animate-in slide-in-from-top-2 duration-200" data-id="element-14681">
          <div className="flex flex-wrap gap-2" data-id="element-14682">
            <button onClick={() => setFilterStatus('all')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-14683">
              Все статусы
            </button>
            <button onClick={() => setFilterStatus('active')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'active' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-300'}`} data-id="element-14684">
              Активные
            </button>
            <button onClick={() => setFilterStatus('hidden')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'hidden' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:border-amber-300'}`} data-id="element-14685">
              Скрытые
            </button>
            <button onClick={() => setFilterStatus('draft')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'draft' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-14686">
              Черновики
            </button>
          </div>
        </Card>}

      {/* BULK ACTIONS BAR */}
      {selectedIds.size > 0 && <div className="bg-indigo-600 text-white p-3 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-4 animate-in slide-in-from-bottom-4 shadow-lg sticky top-4 z-40" data-id="element-14687">
          <div className="text-sm font-medium" data-id="element-14688">
            Выбрано услуг: {selectedIds.size}
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-id="element-14689">
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex-1 sm:flex-none" onClick={() => handleBulkAction('activate')} data-id="element-14690">
              <Eye className="w-4 h-4 mr-1.5" data-id="element-14691" /> Активировать
            </Button>
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex-1 sm:flex-none" onClick={() => handleBulkAction('hide')} data-id="element-14692">
              <EyeOff className="w-4 h-4 mr-1.5" data-id="element-14693" /> Скрыть
            </Button>
            <Button variant="outline" size="sm" className="bg-rose-500/20 border-rose-500/30 text-rose-100 hover:bg-rose-500/40 flex-1 sm:flex-none" onClick={() => handleBulkAction('delete')} data-id="element-14694">
              <Trash2 className="w-4 h-4 mr-1.5" data-id="element-14695" /> Удалить
            </Button>
          </div>
        </div>}

      {/* 6. MAIN CONTENT (TABLE OR CARDS) */}
      {filteredServices.length === 0 ? <Card padding="lg" className="text-center border-dashed border-2 border-slate-200" data-id="element-14696">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-14697">
            <Search className="w-8 h-8 text-slate-300" data-id="element-14698" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-14699">
            Услуги не найдены
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-14700">
            Попробуйте изменить параметры поиска или фильтры.
          </p>
          <Button variant="primary" onClick={() => {
        setSearchQuery('');
        setFilterStatus('all');
      }} data-id="element-14701">
            Сбросить фильтры
          </Button>
        </Card> : viewMode === 'table' /* TABLE VIEW */ ? <Card padding="none" className="border-slate-200 overflow-visible" data-id="element-14702">
          <div className="overflow-x-auto" data-id="element-14703">
            <table className="w-full text-left whitespace-nowrap" data-id="element-14704">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-14705">
                <tr data-id="element-14706">
                  <th className="px-4 py-3 w-10 text-center" data-id="element-14707">
                    <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.size === filteredServices.length && filteredServices.length > 0} onChange={toggleSelectAll} data-id="element-14708" />
                  </th>
                  <th className="px-4 py-3 font-medium" data-id="element-14709">Название</th>
                  <th className="px-4 py-3 font-medium" data-id="element-14710">Цена</th>
                  <th className="px-4 py-3 font-medium" data-id="element-14711">Статус</th>
                  <th className="px-4 py-3 font-medium" data-id="element-14712">Продажи / Выручка</th>
                  <th className="px-4 py-3 font-medium" data-id="element-14713">Конверсия</th>
                  <th className="px-4 py-3 font-medium" data-id="element-14714">Рейтинг</th>
                  <th className="px-4 py-3 text-right font-medium" data-id="element-14715">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-14716">
                {filteredServices.map(s => <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.has(s.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-14717">
                    <td className="px-4 py-3 text-center" data-id="element-14718">
                      <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} data-id="element-14719" />
                    </td>
                    <td className="px-4 py-3" data-id="element-14720">
                      <div className="text-sm font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-2" onClick={() => setEditingService(s)} data-id="element-14721">
                        {s.name}
                        {s.isProblem && <AlertCircle className="w-3.5 h-3.5 text-rose-500" title={s.problemText} data-id="element-14722" />}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-14723">
                        {s.category} • {s.format}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-14724">
                      {s.price.toLocaleString('ru-RU')} ₽
                    </td>
                    <td className="px-4 py-3" data-id="element-14725">
                      <div className="cursor-pointer inline-block" onClick={e => handleStatusToggle(s.id, e)} title="Нажмите для переключения" data-id="element-14726">
                        {s.status === 'active' && <Badge variant="success" size="sm" data-id="element-14727">
                            Активна
                          </Badge>}
                        {s.status === 'draft' && <Badge variant="neutral" size="sm" data-id="element-14728">
                            Черновик
                          </Badge>}
                        {s.status === 'hidden' && <Badge variant="warning" size="sm" data-id="element-14729">
                            Скрыта
                          </Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-14730">
                      <div className="text-sm font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-1" onClick={e => navigateToOrders(s.name, e)} data-id="element-14731">
                        {s.sales} шт. <ChevronRight className="w-3 h-3" data-id="element-14732" />
                      </div>
                      <div className="text-xs font-medium text-emerald-600" data-id="element-14733">
                        {s.revenue > 0 ? `${s.revenue.toLocaleString('ru-RU')} ₽` : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-14734">
                      <div className="text-sm text-slate-700" data-id="element-14735">
                        {s.conversion > 0 ? `${s.conversion}%` : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-14736">
                      {s.rating > 0 ? <div className="flex items-center gap-1 text-sm font-medium text-slate-700" data-id="element-14737">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-14738" />{' '}
                          {s.rating}
                        </div> : <span className="text-slate-400 text-sm" data-id="element-14739">-</span>}
                      {s.returns > 0 && <div className="text-[10px] text-rose-500 mt-0.5" data-id="element-14740">
                          {s.returns} возвратов
                        </div>}
                    </td>
                    <td className="px-4 py-3 text-right relative" data-id="element-14741">
                      <div className="flex items-center justify-end gap-1" data-id="element-14742">
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => setEditingService(s)} data-id="element-14743">
                          <Edit2 className="w-4 h-4" data-id="element-14744" />
                        </Button>
                        <Button variant="ghost" size="sm" className={`w-8 h-8 p-0 ${s.status === 'active' ? 'text-slate-400 hover:text-amber-600' : 'text-slate-400 hover:text-emerald-600'}`} title={s.status === 'active' ? 'Скрыть' : 'Опубликовать'} onClick={e => handleStatusToggle(s.id, e)} data-id="element-14745">
                          {s.status === 'active' ? <EyeOff className="w-4 h-4" data-id="element-14746" /> : <Eye className="w-4 h-4" data-id="element-14747" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-slate-900" onClick={e => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === s.id ? null : s.id);
                  }} data-id="element-14748">
                          <MoreVertical className="w-4 h-4" data-id="element-14749" />
                        </Button>
                      </div>

                      {/* Dropdown Menu */}
                      {activeDropdown === s.id && <div className="absolute right-8 top-10 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()} data-id="element-14750">
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleDuplicate(s, e)} data-id="element-14751">
                            <Copy className="w-4 h-4 text-slate-400" data-id="element-14752" />{' '}
                            Дублировать
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => navigateToPromotion(s.name, e)} data-id="element-14753">
                            <Zap className="w-4 h-4 text-amber-500" data-id="element-14754" />{' '}
                            Продвигать
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    showToast('Публичная страница открыта');
                    setActiveDropdown(null);
                  }} data-id="element-14755">
                            <ExternalLink className="w-4 h-4 text-slate-400" data-id="element-14756" />{' '}
                            Открыть страницу
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-14757"></div>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    showToast('Услуга в архиве');
                    setActiveDropdown(null);
                  }} data-id="element-14758">
                            <Archive className="w-4 h-4 text-slate-400" data-id="element-14759" /> В
                            архив
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    setDeleteConfirmId(s.id);
                    setActiveDropdown(null);
                  }} data-id="element-14760">
                            <Trash2 className="w-4 h-4 text-rose-500" data-id="element-14761" /> Удалить
                          </button>
                        </div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card> /* CARDS VIEW (Mobile friendly) */ : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-14762">
          {filteredServices.map(s => <Card key={s.id} padding="md" className={`border-slate-200 relative ${selectedIds.has(s.id) ? 'ring-2 ring-indigo-500' : ''}`} data-id="element-14763">
              <div className="absolute top-4 right-4 flex gap-2" data-id="element-14764">
                <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-5 h-5" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} data-id="element-14765" />
              </div>

              <div className="mb-4 pr-8" data-id="element-14766">
                <div className="flex items-center gap-2 mb-2" data-id="element-14767">
                  {s.status === 'active' && <Badge variant="success" size="sm" data-id="element-14768">
                      Активна
                    </Badge>}
                  {s.status === 'draft' && <Badge variant="neutral" size="sm" data-id="element-14769">
                      Черновик
                    </Badge>}
                  {s.status === 'hidden' && <Badge variant="warning" size="sm" data-id="element-14770">
                      Скрыта
                    </Badge>}
                  {s.isProblem && <AlertCircle className="w-4 h-4 text-rose-500" title={s.problemText} data-id="element-14771" />}
                </div>
                <h3 className="text-lg font-bold text-slate-900 cursor-pointer hover:text-indigo-600" onClick={() => setEditingService(s)} data-id="element-14772">
                  {s.name}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-14773">
                  {s.category} • {s.format}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 bg-slate-50 p-3 rounded-xl" data-id="element-14774">
                <div data-id="element-14775">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-14776">
                    Цена
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-14777">
                    {s.price.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="cursor-pointer hover:bg-slate-200/50 rounded p-1 -m-1 transition-colors" onClick={e => navigateToOrders(s.name, e)} data-id="element-14778">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-14779">
                    Продажи
                  </div>
                  <div className="font-bold text-indigo-600 flex items-center" data-id="element-14780">
                    {s.sales} <ChevronRight className="w-3 h-3 ml-0.5" data-id="element-14781" />
                  </div>
                </div>
                <div data-id="element-14782">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-14783">
                    Выручка
                  </div>
                  <div className="font-bold text-emerald-600" data-id="element-14784">
                    {s.revenue > 0 ? `${s.revenue.toLocaleString('ru-RU')} ₽` : '-'}
                  </div>
                </div>
                <div data-id="element-14785">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-14786">
                    Конверсия
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-14787">
                    {s.conversion > 0 ? `${s.conversion}%` : '-'}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-14788">
                <div className="flex gap-1" data-id="element-14789">
                  <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={() => setEditingService(s)} data-id="element-14790">
                    <Edit2 className="w-4 h-4 text-slate-600" data-id="element-14791" />
                  </Button>
                  <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={e => handleStatusToggle(s.id, e)} data-id="element-14792">
                    {s.status === 'active' ? <EyeOff className="w-4 h-4 text-amber-600" data-id="element-14793" /> : <Eye className="w-4 h-4 text-emerald-600" data-id="element-14794" />}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" className="text-slate-500" onClick={e => {
            e.stopPropagation();
            setActiveDropdown(activeDropdown === s.id ? null : s.id);
          }} data-id="element-14795">
                  Еще <MoreVertical className="w-4 h-4 ml-1" data-id="element-14796" />
                </Button>
              </div>

              {/* Mobile Dropdown Menu */}
              {activeDropdown === s.id && <div className="absolute right-4 bottom-14 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()} data-id="element-14797">
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleDuplicate(s, e)} data-id="element-14798">
                    <Copy className="w-4 h-4 text-slate-400" data-id="element-14799" /> Дублировать
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => navigateToPromotion(s.name, e)} data-id="element-14800">
                    <Zap className="w-4 h-4 text-amber-500" data-id="element-14801" /> Продвигать
                  </button>
                  <div className="h-px bg-slate-100 my-1" data-id="element-14802"></div>
                  <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => {
            e.stopPropagation();
            setDeleteConfirmId(s.id);
            setActiveDropdown(null);
          }} data-id="element-14803">
                    <Trash2 className="w-4 h-4 text-rose-500" data-id="element-14804" /> Удалить
                  </button>
                </div>}
            </Card>)}
        </div>}
    </div>;
}